import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState, NoDataIcon } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { useAlertRules, useCreateAlertRule, useDeleteAlertRule, useUpdateAlertRule } from '@/hooks/queries/useAlerts';
import { formatAlertType, formatRelativeTime } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { AlertRuleForm } from './AlertRuleForm';
import type { AlertRule, AlertType } from '@steward/shared';

const RECOMMENDED_ALERTS: Array<{
  name: string;
  alertType: AlertType;
  conditions: AlertRule['conditions'];
  cooldownMinutes: number;
  description: string;
}> = [
  {
    name: 'Profit Target 50%',
    alertType: 'profit_target',
    conditions: { thresholdPercent: 50 },
    cooldownMinutes: 60,
    description: 'Alert when a position reaches 50% of max profit — the sweet spot for closing credit spreads.',
  },
  {
    name: 'Loss Limit 100%',
    alertType: 'loss_limit',
    conditions: { thresholdPercent: 100 },
    cooldownMinutes: 60,
    description: 'Alert when losses equal the premium collected — time to consider cutting.',
  },
  {
    name: 'Expiration Warning 7 DTE',
    alertType: 'expiration_warning',
    conditions: { daysThreshold: 7 },
    cooldownMinutes: 120,
    description: 'Heads up when positions are a week from expiration.',
  },
  {
    name: 'Roll Window 21 DTE',
    alertType: 'roll_opportunity',
    conditions: { daysThreshold: 21 },
    cooldownMinutes: 120,
    description: 'Reminder to evaluate rolling when positions hit 21 DTE.',
  },
  {
    name: 'Strike Proximity 5%',
    alertType: 'strike_proximity',
    conditions: { strikeProximityPercent: 5 },
    cooldownMinutes: 60,
    description: 'Warning when the underlying moves within 5% of a short strike.',
  },
];

export function AlertRuleList() {
  const { data: rules, isLoading } = useAlertRules();
  const createRule = useCreateAlertRule();
  const deleteRule = useDeleteAlertRule();
  const updateRule = useUpdateAlertRule();
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [showRecommended, setShowRecommended] = useState(false);
  const [addingRecommended, setAddingRecommended] = useState(false);

  const handleToggleEnabled = (rule: AlertRule) => {
    updateRule.mutate({
      id: rule.id,
      data: { isEnabled: !rule.isEnabled },
    });
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this alert rule?')) {
      deleteRule.mutate(id);
    }
  };

  const handleAddRecommended = async (presets: typeof RECOMMENDED_ALERTS) => {
    setAddingRecommended(true);
    for (const preset of presets) {
      await createRule.mutateAsync({
        name: preset.name,
        alertType: preset.alertType,
        conditions: preset.conditions,
        cooldownMinutes: preset.cooldownMinutes,
        isEnabled: true,
      });
    }
    setAddingRecommended(false);
    setShowRecommended(false);
  };

  // Figure out which recommended alerts aren't already created
  const existingTypes = new Set(rules?.map(r => r.alertType) || []);
  const missingRecommended = RECOMMENDED_ALERTS.filter(r => !existingTypes.has(r.alertType));

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Alert Rules</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Alert Rules</CardTitle>
          <div className="flex items-center gap-2">
            {missingRecommended.length > 0 && (
              <Button variant="secondary" size="sm" onClick={() => setShowRecommended(true)}>
                Add Recommended
              </Button>
            )}
            <Button onClick={() => setShowForm(true)}>Create Rule</Button>
          </div>
        </CardHeader>
        <CardContent>
          {!rules || rules.length === 0 ? (
            <EmptyState
              icon={<NoDataIcon />}
              title="No alert rules"
              description="Create rules to get notified about important events"
              action={
                <div className="flex items-center gap-2">
                  <Button variant="secondary" onClick={() => setShowRecommended(true)}>
                    Add Recommended
                  </Button>
                  <Button onClick={() => setShowForm(true)}>Create Rule</Button>
                </div>
              }
            />
          ) : (
            <div className="space-y-3">
              {rules.map((rule) => (
                <div
                  key={rule.id}
                  className={cn(
                    'p-4 border rounded-lg transition-colors',
                    rule.isEnabled ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">{rule.name}</span>
                        <Badge variant={rule.isEnabled ? 'success' : 'default'} size="sm">
                          {rule.isEnabled ? 'Active' : 'Disabled'}
                        </Badge>
                        <Badge variant="info" size="sm">
                          {formatAlertType(rule.alertType)}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500">
                        {formatConditions(rule)}
                      </p>
                      {rule.lastTriggeredAt && (
                        <p className="text-xs text-gray-400 mt-1">
                          Last triggered {formatRelativeTime(rule.lastTriggeredAt)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleEnabled(rule)}
                      >
                        {rule.isEnabled ? 'Disable' : 'Enable'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingRule(rule);
                          setShowForm(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(rule.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingRule(null);
        }}
        title={editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}
        size="lg"
      >
        <AlertRuleForm
          rule={editingRule}
          onSuccess={() => {
            setShowForm(false);
            setEditingRule(null);
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingRule(null);
          }}
        />
      </Modal>

      <Modal
        isOpen={showRecommended}
        onClose={() => setShowRecommended(false)}
        title="Recommended Alerts"
        size="lg"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-500">
            These alerts cover the most common scenarios for options sellers. Add them all at once or pick individual ones.
          </p>
          {missingRecommended.map((preset) => (
            <div key={preset.alertType} className="p-3 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-medium text-gray-900 text-sm">{preset.name}</span>
                    <Badge variant="info" size="sm">{formatAlertType(preset.alertType)}</Badge>
                  </div>
                  <p className="text-xs text-gray-500">{preset.description}</p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleAddRecommended([preset])}
                  isLoading={addingRecommended}
                >
                  Add
                </Button>
              </div>
            </div>
          ))}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowRecommended(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => handleAddRecommended(missingRecommended)}
              isLoading={addingRecommended}
            >
              Add All ({missingRecommended.length})
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function formatConditions(rule: AlertRule): string {
  const parts: string[] = [];
  const { conditions } = rule;

  if (conditions.thresholdPercent != null) {
    parts.push(`${conditions.thresholdPercent}% threshold`);
  }
  if (conditions.daysThreshold != null) {
    parts.push(`${conditions.daysThreshold} days`);
  }
  if (conditions.strikeProximityPercent != null) {
    parts.push(`${conditions.strikeProximityPercent}% strike proximity`);
  }
  if (conditions.extrinsicThreshold != null) {
    parts.push(`≤ $${conditions.extrinsicThreshold} extrinsic`);
  }
  if (conditions.symbols?.length) {
    parts.push(`Symbols: ${conditions.symbols.join(', ')}`);
  }

  return parts.length > 0 ? parts.join(' • ') : 'No conditions configured';
}
